import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import useTicketTabsStore from "@/stores/ticketTabsStore";

const getStatusColor = (status: string): string => {
  switch (status) {
    case "new":
      return "bg-blue-100 text-blue-700";
    case "open":
      return "bg-green-100 text-green-700";
    case "pending":
      return "bg-yellow-100 text-yellow-700";
    case "in_progress":
      return "bg-purple-100 text-purple-700";
    case "closed":
      return "bg-slate-100 text-slate-600";
    default:
      return "bg-slate-100 text-slate-600";
  }
};

const formatTicketNumber = (ticketNumber?: string): string => {
  return ticketNumber || '';
};

const TicketTabsBar: React.FC = () => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const { openTickets: rawOpenTickets, activeTabId, setActiveTab, removeTab, lastViewPath } = useTicketTabsStore();

  // Filter out any invalid tickets
  const openTickets = (rawOpenTickets || []).filter(t => t && t.id && typeof t.id === 'string');

  const updateArrowVisibility = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateArrowVisibility();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", updateArrowVisibility);
      window.addEventListener("resize", updateArrowVisibility);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", updateArrowVisibility);
      }
      window.removeEventListener("resize", updateArrowVisibility);
    };
  }, [updateArrowVisibility, openTickets]);

  const handleScroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 200;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const handleTabClick = (ticketId: string) => {
    setActiveTab(ticketId);
    navigate(`/tickets/${ticketId}`);
  };

  const handleCloseTab = (ticketId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    const currentIndex = openTickets.findIndex(t => t.id === ticketId);
    const isActiveTab = activeTabId === ticketId;
    const remaining = openTickets.filter(t => t.id !== ticketId);

    removeTab(ticketId);

    // Aktif sekme kapatıldıysa mutlaka rotadan çık — yoksa açık ticket sayfası
    // sekmeyi yeniden ekleyip "kapanmıyor" izlenimi veriyordu.
    if (isActiveTab) {
      if (remaining.length > 0) {
        // Komşu sekmeye geç (kapatılanın yerindeki, yoksa bir önceki)
        const nextTicket = remaining[Math.min(currentIndex, remaining.length - 1)];
        setActiveTab(nextTicket.id);
        navigate(`/tickets/${nextTicket.id}`);
      } else {
        // Son sekmeydi → en son gelinen ANSICHT görünümüne dön
        navigate(lastViewPath || '/tickets/all');
      }
    }
  };

  if (openTickets.length === 0) {
    return null;
  }

  return (
    <div className="relative flex items-center">
      {/* Left scroll button */}
      {showLeftArrow && (
        <button
          onClick={() => handleScroll("left")}
          className="absolute left-0 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow-md backdrop-blur-sm transition hover:bg-slate-50 hover:text-slate-700"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}

      {/* Tabs container */}
      <div
        ref={scrollContainerRef}
        className={cn(
          "flex items-center gap-1 overflow-x-auto scrollbar-hide",
          showLeftArrow && "ml-8",
          showRightArrow && "mr-8"
        )}
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {openTickets.map((ticket) => (
          <div
            key={ticket.id}
            className={cn(
              "group relative flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 transition-all flex-shrink-0",
              activeTabId === ticket.id
                ? "border-[#0F99FD]/30 bg-[#0F99FD]/5 text-[#0F99FD]"
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
            )}
            onClick={() => handleTabClick(ticket.id)}
          >
            {/* Status indicator dot */}
            <span
              className={cn(
                "h-2 w-2 rounded-full flex-shrink-0",
                ticket.status === "new" && "bg-blue-500",
                ticket.status === "open" && "bg-green-500",
                ticket.status === "pending" && "bg-yellow-500",
                ticket.status === "in_progress" && "bg-purple-500",
                ticket.status === "closed" && "bg-slate-400"
              )}
            />

            {/* Ticket Subject (first 20 chars) */}
            <span
              className={cn(
                "text-xs font-medium whitespace-nowrap",
                activeTabId === ticket.id ? "text-[#0F99FD]" : "text-slate-700"
              )}
              title={ticket.subject}
            >
              {ticket.subject?.slice(0, 20)}{ticket.subject?.length > 20 ? '...' : ''}
            </span>

            {/* Close button */}
            <button
              onClick={(e) => handleCloseTab(ticket.id, e)}
              className={cn(
                "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full transition-colors",
                activeTabId === ticket.id
                  ? "text-[#0F99FD]/60 hover:bg-[#0F99FD]/10 hover:text-[#0F99FD]"
                  : "text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-slate-200 hover:text-slate-600"
              )}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Right scroll button */}
      {showRightArrow && (
        <button
          onClick={() => handleScroll("right")}
          className="absolute right-0 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow-md backdrop-blur-sm transition hover:bg-slate-50 hover:text-slate-700"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default TicketTabsBar;
